/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';

const TIME_SERIES_X_AXIS_COLUMN = 'local server time';
const TIME_SERIES_IGNORED_COLUMNS = new Set(['timestamp', 'state']);

export interface ParsedTimeSeriesCsv {
    columns: string[];
    plotXAxisColumn: string | null;
    plotXAxisValues: string[];
    plotYAxisColumns: string[];
    plotYAxisValues: Record<string, number[]>;
}

function getVisibleTimeSeriesColumns(fields: string[]): string[] {
    return fields.filter(
        (field) =>
            field.trim() !== '' && !TIME_SERIES_IGNORED_COLUMNS.has(field),
    );
}

export function parseTimeSeriesCsv(csv: string): ParsedTimeSeriesCsv {
    const plotXAxisValues: string[] = [];
    const plotYAxisValues: Record<string, number[]> = {};
    let columns: string[] = [];
    let plotYAxisColumns: string[] = [];

    function initializeColumns(fields: string[] | undefined): void {
        if (columns.length > 0 || !fields) {
            return;
        }

        columns = getVisibleTimeSeriesColumns(fields);
        plotYAxisColumns = columns.filter(
            (column) => column !== TIME_SERIES_X_AXIS_COLUMN,
        );

        plotYAxisColumns.forEach((column) => {
            plotYAxisValues[column] = [];
        });
    }

    Papa.parse<Record<string, string>>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
        step: (results) => {
            initializeColumns(results.meta.fields);

            if (plotYAxisColumns.length === 0) {
                return;
            }

            const xValue = results.data[TIME_SERIES_X_AXIS_COLUMN]?.trim() ?? '';
            if (!xValue) {
                return;
            }

            plotXAxisValues.push(xValue);

            plotYAxisColumns.forEach((column) => {
                const yValue = results.data[column]?.trim() ?? '';
                const parsedYValue = Number.parseFloat(yValue);

                plotYAxisValues[column].push(Number.isNaN(parsedYValue) ? Number.NaN : parsedYValue);
            });
        },
        complete: (results) => {
            initializeColumns(results.meta.fields);
        },
    });

    initializeColumns(csv.split(/\r?\n/, 1)[0]?.split(';'));

    return {
        columns,
        plotXAxisColumn: columns.includes(TIME_SERIES_X_AXIS_COLUMN)
            ? TIME_SERIES_X_AXIS_COLUMN
            : null,
        plotXAxisValues,
        plotYAxisColumns,
        plotYAxisValues,
    };
}
